import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, BookOpen, Clock, Users, Play, CheckCircle, Upload, Eye, Edit,
  Tag, Award, FileText, BarChart3, Loader2, AlertCircle, UserPlus, Building2
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import ScormPlayer from "./ScormPlayer";

const courseTypeLabels = {
  "scorm_1.2": "eLearning",
  "scorm_2004": "eLearning",
  ilt: "Instructor-Led Training",
  video: "Video",
  document: "Document"
};

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showScormPlayer, setShowScormPlayer] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignMode, setAssignMode] = useState("individual");
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignDept, setAssignDept] = useState("");
  const [assignmentType, setAssignmentType] = useState("mandatory");
  const [departments, setDepartments] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const isAdmin = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.role === "trainer" || user?.role === "manager";

  useEffect(() => { fetchCourse(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-start course when coming from MyProgress with ?autostart=true
  const autostartTriggered = useRef(false);
  useEffect(() => {
    if (autostartTriggered.current) return;
    const shouldAutostart = searchParams.get("autostart") === "true";
    if (shouldAutostart && course && !loading) {
      autostartTriggered.current = true;
      // Clean URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("autostart");
      setSearchParams(newParams, { replace: true });
      // Launch
      if (enrollment) {
        handleLaunchCourse(enrollment);
      } else {
        handleEnrollAndStart();
      }
    }
  }, [course, enrollment, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCourse = async () => {
    try {
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);
      // Check if current user is enrolled (any role)
      if (user) {
        try {
          const enrollmentsRes = await api.get(`/enrollments?course_id=${courseId}&user_id=${user.id}`);
          if (enrollmentsRes.data?.length > 0) setEnrollment(enrollmentsRes.data[0]);
        } catch (e) {}
      }
    } catch (error) {
      toast.error("Failed to load course");
    } finally { setLoading(false); }
  };

  const handleEnroll = async () => {
    try {
      const res = await api.post("/enrollments", { course_id: courseId });
      setEnrollment(res.data);
      toast.success("Successfully enrolled!");
      return res.data;
    } catch (error) {
      if (error.response?.data?.detail?.includes("already enrolled")) {
        // Already enrolled, fetch enrollment
        const enrollmentsRes = await api.get(`/enrollments?course_id=${courseId}&user_id=${user.id}`);
        if (enrollmentsRes.data?.length > 0) {
          setEnrollment(enrollmentsRes.data[0]);
          return enrollmentsRes.data[0];
        }
      }
      toast.error(error.response?.data?.detail || "Failed to enroll");
      return null;
    }
  };

  const handleEnrollAndStart = async () => {
    const enr = await handleEnroll();
    if (enr) {
      // Small delay then launch
      setTimeout(() => handleLaunchCourse(enr), 300);
    }
  };

  const handleUploadSCORM = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStage("prepare");
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      setUploadStage("upload");
      await api.post(`/courses/${courseId}/upload-scorm`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          if (pct < 50) {
            setUploadProgress(pct);
            setUploadStage("upload");
          } else if (pct < 80) {
            setUploadProgress(pct);
            setUploadStage("extract");
          } else if (pct < 95) {
            setUploadProgress(pct);
            setUploadStage("parse");
          } else {
            setUploadProgress(pct > 95 ? 95 : pct);
            setUploadStage("validate");
          }
        }
      });
      setUploadProgress(100);
      setUploadStage("done");
      toast.success("SCORM package uploaded successfully!");
      setTimeout(() => {
        setShowUploadDialog(false);
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadStage("");
        fetchCourse();
      }, 1200);
    } catch (error) {
      setUploadStage("error");
      setUploadProgress(0);
      toast.error(error.response?.data?.detail || "Failed to upload SCORM package");
      setTimeout(() => setUploadStage(""), 2000);
    } finally { setUploading(false); }
  };

  const handleLaunchCourse = (enr) => {
    const activeEnrollment = enr || enrollment;
    if (course?.content_slides?.length > 0) {
      navigate(`/learn/${courseId}`);
    } else if (course?.course_type?.includes("scorm") && course?.scorm_entry_point) {
      if (activeEnrollment) setEnrollment(activeEnrollment);
      setShowScormPlayer(true);
    } else {
      // No launchable content available
      toast.info("Course content is not yet available. Please check back later.");
    }
  };

  const openAssignDialog = async () => {
    setShowAssignDialog(true);
    try {
      const [filtersRes, usersRes] = await Promise.all([
        api.get("/analytics/filters"),
        api.get("/users"),
      ]);
      setDepartments(filtersRes.data?.departments || []);
      const learners = (usersRes.data || []).filter(u => u.role === "learner");
      setTeamUsers(learners);
    } catch (e) {}
  };

  const handleBulkAssign = async () => {
    setAssigning(true);
    try {
      const payload = { course_ids: [courseId], assignment_type: assignmentType };
      if (assignMode === "individual") {
        payload.user_ids = selectedUserIds;
      } else if (assignMode === "department") {
        payload.department = assignDept;
      }
      const res = await api.post("/courses/bulk-assign", payload);
      toast.success(res.data?.message || "Assigned!");
      setShowAssignDialog(false);
      setSelectedUserIds([]);
      setAssignDept("");
      fetchCourse();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign");
    } finally { setAssigning(false); }
  };

  const toggleUser = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  };

  if (loading) return (
    <AdminLayout title="Course Details" variant={user?.role === "super_admin" ? "super_admin" : "tenant_admin"}>
      <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!course) return (
    <AdminLayout title="Course Details" variant={user?.role === "super_admin" ? "super_admin" : "tenant_admin"}>
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Course not found</h2>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    </AdminLayout>
  );

  const isILT = course.course_type === "ilt";
  const hasScorm = course.course_type?.includes("scorm") && course.scorm_entry_point;

  return (
    <AdminLayout title="Course Details" variant={user?.role === "super_admin" ? "super_admin" : "tenant_admin"}>
      <div className="max-w-6xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 -ml-2">
          <ArrowLeft className="w-4 h-4" /> Back to Courses
        </Button>

        {/* Hero Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl"
          style={{
            background: course.thumbnail_url
              ? `linear-gradient(to right, rgba(15,23,42,0.95), rgba(15,23,42,0.7)), url(${course.thumbnail_url}) center/cover no-repeat`
              : "linear-gradient(to br, #0f172a, #1e293b, #0f172a)"
          }}
        >
          <div className="relative z-10 p-8 md:p-12">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge className="bg-primary/20 text-primary border-primary/30">
                    {courseTypeLabels[course.course_type] || course.course_type}
                  </Badge>
                  <Badge variant="outline" className="border-white/30 text-white/70">{course.category}</Badge>
                  {course.source === "tts" && <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">TTS</Badge>}
                  {isILT && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Purchase Separately</Badge>}
                  {!course.is_published && <Badge className="bg-warning/20 text-warning border-warning/30">Draft</Badge>}
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Sora', sans-serif" }} data-testid="course-title">
                  {course.title}
                </h1>

                <p className="text-white/70 text-lg mb-6 max-w-2xl">
                  {course.description || "No description available."}
                </p>

                <div className="flex flex-wrap items-center gap-6 mb-6">
                  <div className="flex items-center gap-2 text-white/70">
                    <Clock className="w-5 h-5" /><span>{course.duration_hours} hours</span>
                  </div>
                  {course.enrolled_count > 0 && (
                    <div className="flex items-center gap-2 text-white/70" data-testid="enrolled-count">
                      <Users className="w-5 h-5" /><span>{course.enrolled_count} enrolled</span>
                    </div>
                  )}
                </div>

                {course.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {course.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="border-white/20 text-white/60">
                        <Tag className="w-3 h-3 mr-1" />{tag}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {enrollment ? (
                    <Button className="btn-primary gap-2" onClick={() => handleLaunchCourse()} data-testid="continue-course-btn">
                      <Play className="w-4 h-4" />
                      {enrollment.status === "completed" ? "Review Course" : "Continue Learning"}
                    </Button>
                  ) : (
                    <Button className="btn-primary gap-2" onClick={handleEnrollAndStart} data-testid="enroll-btn">
                      <BookOpen className="w-4 h-4" /> Enroll & Start
                    </Button>
                  )}
                  {isAdmin && (
                    <>
                      <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 gap-2" onClick={openAssignDialog} data-testid="assign-course-btn">
                        <UserPlus className="w-4 h-4" /> Assign Course
                      </Button>
                      {course.course_type?.includes("scorm") && course.source === "tenant" && (
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 gap-2" onClick={() => setShowUploadDialog(true)} data-testid="upload-scorm-btn">
                          <Upload className="w-4 h-4" /> {course.scorm_entry_point ? "Replace SCORM" : "Upload SCORM"}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Subscription Card */}
              <div className="md:w-72 shrink-0">
                <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      {isILT ? (
                        <div>
                          <div className="text-lg font-bold text-amber-400">Purchase Separately</div>
                          <p className="text-xs text-white/50 mt-1">Not included in subscription</p>
                        </div>
                      ) : course.out_of_package ? (
                        <div>
                          <div className="text-lg font-bold text-amber-400">Out of Package</div>
                          <p className="text-xs text-white/50 mt-1">Contact TTS for pricing</p>
                        </div>
                      ) : (
                        <div>
                          <div className="text-lg font-bold text-green-400">Included</div>
                          <p className="text-xs text-white/50 mt-1">Part of your TTS subscription</p>
                        </div>
                      )}
                    </div>

                    {enrollment && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-sm text-white/70 mb-2">
                          <span>Your Progress</span><span>{enrollment.progress_percent || enrollment.progress || 0}%</span>
                        </div>
                        <Progress value={enrollment.progress_percent || enrollment.progress || 0} className="h-2 bg-white/10" />
                        <div className="mt-2">
                          <Badge className={
                            enrollment.status === "completed" ? "bg-success/20 text-success" :
                            enrollment.status === "in_progress" ? "bg-warning/20 text-warning" :
                            "bg-muted text-muted-foreground"
                          }>
                            {enrollment.status === "completed" && <CheckCircle className="w-3 h-3 mr-1" />}
                            {enrollment.status.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}
                          </Badge>
                        </div>
                        {enrollment.status === "completed" && (
                          <Button className="w-full mt-3 bg-green-600 hover:bg-green-700 gap-2" data-testid="detail-download-certificate-btn"
                            onClick={async () => {
                              try {
                                const response = await api.get(`/enrollments/${enrollment.id}/certificate`, { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', `certificate_${course.title.replace(/\s+/g, '_')}.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                                toast.success("Certificate downloaded!");
                              } catch (error) { toast.error("Failed to download certificate"); }
                            }}
                          >
                            <Award className="w-4 h-4" /> Certificate of Completion
                          </Button>
                        )}
                      </div>
                    )}

                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2 text-white/70">
                        <CheckCircle className="w-4 h-4 text-success" /><span>Certificate of Completion</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <CheckCircle className="w-4 h-4 text-success" /><span>Self-paced learning</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/70">
                        <CheckCircle className="w-4 h-4 text-success" /><span>Progress tracking & resume</span>
                      </div>
                      {isILT && (
                        <div className="flex items-center gap-2 text-white/70">
                          <CheckCircle className="w-4 h-4 text-success" /><span>Live instructor sessions</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Course Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> Course Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {course.description || "This course provides comprehensive training on the subject matter."}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /> Course Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{course.duration_hours} hours</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <Badge variant="outline">{courseTypeLabels[course.course_type]}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={course.is_published ? "default" : "secondary"}>
                    {course.is_published ? "Published" : "Draft"}
                  </Badge>
                </div>
                {course.enrolled_count > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Enrolled</span>
                    <span className="font-medium">{course.enrolled_count}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-primary" /> Certificate</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Complete this course to receive your Certificate of Completion.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* SCORM Player Overlay */}
      {showScormPlayer && (
        <ScormPlayer
          course={course}
          enrollmentId={enrollment?.id}
          onClose={() => { setShowScormPlayer(false); fetchCourse(); }}
          onProgressUpdate={fetchCourse}
        />
      )}

      {/* Assign Course Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> Assign Course</DialogTitle>
            <DialogDescription>Assign "{course.title}" to individuals or an entire department.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Assignment Type */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Assignment Type</Label>
              <div className="flex gap-2">
                <Button variant={assignmentType === "mandatory" ? "default" : "outline"} size="sm"
                  onClick={() => setAssignmentType("mandatory")} data-testid="assign-type-mandatory"
                  className={assignmentType === "mandatory" ? "bg-red-600 hover:bg-red-700" : ""}>
                  Mandatory
                </Button>
                <Button variant={assignmentType === "recommended" ? "default" : "outline"} size="sm"
                  onClick={() => setAssignmentType("recommended")} data-testid="assign-type-recommended"
                  className={assignmentType === "recommended" ? "bg-blue-600 hover:bg-blue-700" : ""}>
                  Recommended
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              {["individual", "department"].map(mode => (
                <Button key={mode} variant={assignMode === mode ? "default" : "outline"} size="sm" onClick={() => setAssignMode(mode)} data-testid={`assign-mode-${mode}`}>
                  {mode === "individual" ? <Users className="w-3.5 h-3.5 mr-1" /> : <Building2 className="w-3.5 h-3.5 mr-1" />}
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </Button>
              ))}
            </div>

            {assignMode === "individual" && (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {teamUsers.map(u => (
                  <div key={u.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedUserIds.includes(u.id) ? "border-primary bg-primary/5" : "hover:bg-muted/30"}`}
                    onClick={() => toggleUser(u.id)}
                    data-testid={`assign-user-${u.id}`}
                  >
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${selectedUserIds.includes(u.id) ? "bg-primary border-primary" : "border-muted-foreground/30"}`}>
                      {selectedUserIds.includes(u.id) && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.full_name}</p>
                      <p className="text-[10px] text-muted-foreground">{u.department || u.email}</p>
                    </div>
                  </div>
                ))}
                {teamUsers.length === 0 && <p className="text-center py-4 text-sm text-muted-foreground">No learners found</p>}
              </div>
            )}

            {assignMode === "department" && (
              <Select value={assignDept} onValueChange={setAssignDept}>
                <SelectTrigger data-testid="assign-dept-select">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign} disabled={assigning || (assignMode === "individual" && selectedUserIds.length === 0) || (assignMode === "department" && !assignDept)} data-testid="confirm-assign-btn">
              {assigning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
              Assign ({assignMode === "individual" ? `${selectedUserIds.length} users` : assignDept || "dept"})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SCORM Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(v) => !uploading && setShowUploadDialog(v)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{uploading || uploadStage === "done" ? "Processing SCORM Package" : "Upload SCORM Package"}</DialogTitle>
            {!uploading && uploadStage !== "done" && (
              <DialogDescription>Upload a SCORM package (.zip) for this course.</DialogDescription>
            )}
          </DialogHeader>

          {/* Preloader — shown during upload */}
          {(uploading || uploadStage === "done") ? (
            <div className="py-6" data-testid="scorm-upload-progress">
              {/* Circular progress */}
              <div className="flex justify-center mb-6">
                <div className="relative w-28 h-28">
                  <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
                    <circle cx="50" cy="50" r="42" fill="none" strokeWidth="6"
                      className={uploadStage === "done" ? "text-emerald-500" : uploadStage === "error" ? "text-red-500" : "text-primary"}
                      strokeDasharray={264}
                      strokeDashoffset={264 - (264 * uploadProgress / 100)}
                      strokeLinecap="round"
                      style={{ transition: "stroke-dashoffset 0.4s ease" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {uploadStage === "done" ? (
                      <CheckCircle className="w-8 h-8 text-emerald-500" />
                    ) : uploadStage === "error" ? (
                      <AlertCircle className="w-8 h-8 text-red-500" />
                    ) : (
                      <>
                        <span className="text-2xl font-bold tracking-tight">{uploadProgress}%</span>
                        <span className="text-[10px] text-muted-foreground">completed</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Stage steps */}
              <div className="space-y-3 max-w-sm mx-auto">
                {[
                  { key: "prepare", label: "Preparing upload", desc: "Reading file and creating upload stream", threshold: 0 },
                  { key: "upload", label: "Uploading to server", desc: "Transferring SCORM zip to the server", threshold: 1 },
                  { key: "extract", label: "Extracting contents", desc: "Unzipping package and locating manifest", threshold: 50 },
                  { key: "parse", label: "Parsing manifest", desc: "Reading imsmanifest.xml for metadata", threshold: 80 },
                  { key: "validate", label: "Validating structure", desc: "Verifying SCORM compliance and entry point", threshold: 95 },
                  { key: "done", label: "Complete", desc: "SCORM package ready for learners", threshold: 100 },
                ].map((step, i) => {
                  const stageOrder = ["prepare", "upload", "extract", "parse", "validate", "done"];
                  const currentIdx = stageOrder.indexOf(uploadStage);
                  const stepIdx = stageOrder.indexOf(step.key);
                  const isActive = stepIdx === currentIdx;
                  const isDone = stepIdx < currentIdx || uploadStage === "done";
                  const isPending = stepIdx > currentIdx;

                  return (
                    <div key={step.key} className="flex items-start gap-3" data-testid={`upload-stage-${step.key}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-all duration-300 ${
                        isDone ? "bg-emerald-500 text-white" :
                        isActive ? "bg-primary text-primary-foreground ring-4 ring-primary/20" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {isDone ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : isActive ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <span className="text-[10px] font-bold">{i + 1}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-medium transition-colors ${isDone ? "text-emerald-600 dark:text-emerald-400" : isActive ? "text-foreground" : "text-muted-foreground"}`}>
                          {step.label}
                        </p>
                        {(isActive || isDone) && (
                          <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* File info */}
              {selectedFile && (
                <div className="mt-5 pt-4 border-t text-center text-xs text-muted-foreground">
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                </div>
              )}
            </div>
          ) : (
            /* File picker — shown before upload */
            <div className="py-4">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById("scorm-file-input").click()}>
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                {selectedFile ? (
                  <div><p className="font-medium">{selectedFile.name}</p><p className="text-sm text-muted-foreground mt-1">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p></div>
                ) : (
                  <div><p className="font-medium">Click to select SCORM package</p><p className="text-sm text-muted-foreground mt-1">Supports .zip files (SCORM 1.2 & 2004)</p></div>
                )}
              </div>
              <input id="scorm-file-input" type="file" accept=".zip" className="hidden" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            </div>
          )}

          {/* Footer — hide during upload progress */}
          {!uploading && uploadStage !== "done" && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
              <Button onClick={handleUploadSCORM} disabled={!selectedFile}>
                <Upload className="w-4 h-4 mr-2" />Upload
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CourseDetailPage;
